import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Country } from 'app/profile/models/masterdata.model';
import { EmailDomain, EmailDomainPropertyValue, SupplierAddress, SupplierAddressPropertyValues, SupplierBank, SupplierContactPropertyValue, SupplierDiversityPropertyValue, SupplierDocument, SupplierDocumentPropertyValues, SupplierTax, SupplierTaxPropertyValues } from 'app/profile/models/supplier.model';
import { UserDetails } from 'app/profile/models/user.model';
import { SupplierBankPropertyValue } from 'app/profile/models/supplier.model';
import { OnboardingDocument, SupplierDiversity } from 'app/profile/models/onboarding.model';
import { SupplierTaxInfo } from 'app/profile/models/tax.model';
import { MaskedFieldControl } from 'app/common/models/masked-field.model';
import { AppSettings } from 'app/app.settings';
import { AuthData } from '@tesla/ng-auth';

function deepfind(obj, path) {
  return path.split('.').reduce(function (o, x) {
    return typeof o === 'undefined' || o === null ? o : o[x];
  }, obj);
}

function validateAllFormFields(formGroup: FormGroup) {
  Object.keys(formGroup.controls).map(field => {
    const control = formGroup.get(field);
    if (control instanceof FormControl) {
      control.markAsTouched({ onlySelf: true });
    }
    else if(control instanceof MaskedFieldControl)
    {
      control.fieldControl.markAsTouched({onlySelf : true});
    }
    else if (control instanceof FormGroup) {
      validateAllFormFields(control);
    } else if (control instanceof FormArray) {
      Object.keys(control.controls).map(item => {
        validateAllFormFields(control.controls[item] as FormGroup);
      });
    }
  });
}

export const validateAllFormGroupsInArrayForm = (formArray: FormArray) => {
  if (formArray && formArray.controls) {
    formArray.controls.forEach(control => validateAllFormFields(control as FormGroup));
  }
}

export const extractDomainFromEmail = (email: string): string | null => {
  // Regular expression to match the domain part of an email address
  const domainRegex = /@([a-zA-Z0-9.-]+)$/;

  // Use match method to find the domain part
  const match = email.match(domainRegex);

  // If match is found, return the domain part (group 1), else return null
  return match ? match[1] : null;
}

function ValidationMessages(controlName: string, control: AbstractControl) {
  if (control.hasError('required')) {
    return `${controlName} is required`;
  } else if (control.hasError('minlength') || control.hasError('maxlength')) {
    return `${controlName} must be in range`;
  }
}

function GetFullAddress(address: SupplierAddressPropertyValues, type: string) {
  if (address) {
    return [deepfind(address.street1, type),
    deepfind(address.street2, type),
    deepfind(address.street3, type)].filter(x => x).join(', ');
  }
}

function GetFullAddressDetails(address: SupplierAddressPropertyValues, type: string) {
  if (address) {
    return [deepfind(address.city, type),
    deepfind(address, 'state.' + type), deepfind(address, 'zipCode.' + type)].filter(x => x).join(', ');
  }
}

function GetCountryDetails(address: SupplierAddressPropertyValues, type: string, countries?: Country[]) {
  if (address) {
    let country = null;
    const countryCode = deepfind(address.countryCode, type);
    if (countries && deepfind(address.countryCode, type)) {
      if (countries.find(x => x.abbreviation === countryCode))
        country = countries.find(x => x.abbreviation === countryCode).name;
    }
    return [country || countryCode].filter(x => x).join(', ');
  }
}

function GetFullName(contact: SupplierContactPropertyValue, type: string) {
  if (contact && type) {
    return [deepfind(contact.firstName, type), deepfind(contact.lastName, type)].filter(x => x).join(' ');
  }
}

const getNestedNode = (p, o) => deepfind(o, p.join('.'));

const isNullOrEmpty = (obj) => obj == null || Object.keys(obj).length === 0;

const isNullOrUndefined = (obj) => obj === null || obj === undefined;

const inBoth = (list1, list2) =>
  list1.filter(value => -1 !== list2.indexOf(value));

const intersect = (items: any[][]) => {
  return items.reduce((a, b) => inBoth(a, b));
}

export const removeDuplicates = (data: any[]) => {
  return Array.from(new Set(data));
};

function getSupplierProfileAdmin(user: UserDetails) {
  var profileAdmin = [];
  if (user && user.supplierContact && user.supplierContact.supplierMaps) {
    user.supplierContact.supplierMaps.forEach(x => {
      if (x && x.userRoles) {
        var userRole = x.userRoles.find(y => y.role.toLowerCase() === "profile admin");
        if (userRole) {
          profileAdmin.push(x.supplierCode);
        }
      }
    });
  }
  return profileAdmin;
}

export class Guid {
  static newGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      // tslint:disable-next-line:no-bitwise
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

function updateValueAndValidity(formGroup: FormGroup) {
  Object.keys(formGroup.controls).map(field => {
    const control = formGroup.get(field);
    if (control instanceof FormControl) {
      control.updateValueAndValidity();
    } else if (control instanceof FormGroup) {
      updateValueAndValidity(control);
    } else if (control instanceof FormArray) {
      Object.keys(control.controls).map(item => {
        updateValueAndValidity(control.controls[item] as FormGroup);
      });
    }
  });
}

export const isEqual = function (value, other) {

  // Get the value type
  const type = Object.prototype.toString.call(value);

  // If the two objects are not the same type, return false
  if (type !== Object.prototype.toString.call(other)) { return false; }

  // If items are not an object or array, do a simple comparison
  if (['[object Array]', '[object Object]'].indexOf(type) < 0) { return value === other; }

  // Compare the length of the length of the two items
  const valueLen = type === '[object Array]' ? value.length : Object.keys(value).length;
  const otherLen = type === '[object Array]' ? other.length : Object.keys(other).length;
  if (valueLen !== otherLen) { return false; }

  // Compare two items
  const compare = function (item1, item2) {

    // Get the object type
    const itemType = Object.prototype.toString.call(item1);

    // If an object or array, compare recursively
    if (['[object Array]', '[object Object]'].indexOf(itemType) >= 0) {
      if (!isEqual(item1, item2)) { return false; }
    } else {
      // Otherwise, do a simple comparison
      // If the two items are not the same type, return false
      if (itemType !== Object.prototype.toString.call(item2)) { return false; }

      // Else if it's a function, convert to a string and compare
      // Otherwise, just compare
      if (itemType === '[object Function]') {
        if (item1.toString() !== item2.toString()) { return false; }
      } else {
        if (item1 !== item2) { return false; }
      }

    }
  };

  // Compare properties
  if (type === '[object Array]') {
    for (let i = 0; i < valueLen; i++) {
      if (compare(value[i], other[i]) === false) { return false; }
    }
  } else {
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        if (compare(value[key], other[key]) === false) { return false; }
      }
    }
  }

  // If nothing failed, return true
  return true;

};

export const isCallBackRequired = (token: string): boolean => {
  if (!token) {
    return false;
  }

  return token?.length === 32
}

export const isTokenEffective = (token: string): boolean => {
  if (!token) {
    return false;
  }

  const tokenData = parseDataFromToken(token);
  if (tokenData && tokenData.hasOwnProperty('exp')) {
    const utcTimestamp = new Date().getTime() / 1000;
    return utcTimestamp < tokenData['exp'];
  }
  return false;
}

export const onMFAEnrollmentClick = (configuration: AppSettings, authData: AuthData, email: string, language: string) : void => {
  let mfaUrl = configuration.Production ? `https://mfa.tesla.com/begin` : `https://mfa-stage.tesla.com/begin`;
    const ou_code = JSON.parse(authData.urlBase64Decode(authData.getAccessToken().accessToken)).ou_code
    if (ou_code === "CN") {
      mfaUrl = configuration.Production ? `https://mfa.tesla.cn/begin` : `https://mfa-stage.tesla.cn/begin`;
    }
    const profileCallbackUrl = `${configuration.MFA_ENROLLMENT_CALLBACK_URL}auth/mfa-enrollment/callback`;
    const url = `${mfaUrl}?login_hint=${email}&locale=${language}&post_enrollment_redirect_uri=${profileCallbackUrl}`;
    window.location.href = url;
}

export const showMFADialog = (authData: AuthData, userDetails: UserDetails): boolean => {
    let decodedAccessToken = JSON.parse(authData.urlBase64Decode(authData.getAccessToken().accessToken));
    if(userDetails.isOptedForMFA == true && decodedAccessToken?.amr?.includes('mfa') == false){
      return true;
    }
    return false;
};

const parseDataFromToken = (token: string): {} => {
  try {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export const transformEmailDomain = (supplierData: EmailDomainPropertyValue): EmailDomain => {
  if (!supplierData) {
    return null;
  }
  var result = new EmailDomain();
  Object.keys(supplierData).forEach(name => {
    result[name] = getNestedNode([name, 'newValue'], supplierData);
  });

  return result;
}

export const isNotNullOrUndefined = (val: any): boolean => typeof val !== 'undefined' && val !== null;

export const getSupplierBank = (bankPropertyValue: SupplierBankPropertyValue): SupplierBank => {
  const bank = new SupplierBank();
  if (!bankPropertyValue) {
    return bank;
  }
  bank.Operation = bankPropertyValue.operation?.newValue;
  bank.SupplierCode = bankPropertyValue?.supplierCode;
  bank.PaymentOrigin = bankPropertyValue.paymentOrigin?.newValue;
  bank.ID = bankPropertyValue.id?.newValue;
  bank.StageID = bankPropertyValue.stageID?.newValue;
  bank.BankName = bankPropertyValue.bankName?.newValue;
  bank.BeneficiaryNameEnglish = bankPropertyValue.beneficiaryNameEnglish?.newValue;
  bank.BeneficiaryNameDifferentReason = bankPropertyValue.beneficiaryNameDifferentReason?.newValue;
  bank.BeneficiaryNameDifferentReasonComment = bankPropertyValue.beneficiaryNameDifferentReasonComment?.newValue;
  bank.BankLocalName = bankPropertyValue.bankLocalName?.newValue;
  bank.BeneficiaryNameLocal = bankPropertyValue.beneficiaryNameLocal?.newValue;
  bank.DisplayName = bankPropertyValue.displayName?.newValue;
  bank.BankNickName = bankPropertyValue.bankNickName?.newValue;
  bank.CurrencyCode = bankPropertyValue.currencyCode?.newValue;
  bank.CountryCode = bankPropertyValue.countryCode?.newValue;
  bank.MethodOfPaymentID = bankPropertyValue.methodOfPaymentID?.newValue;
  bank.PaymentType = bankPropertyValue.paymentType?.newValue;
  bank.BankAccountType = bankPropertyValue.bankAccountType?.newValue;
  bank.BankAccountNameKatanaka = bankPropertyValue.bankAccountNameKatanaka?.newValue;
  bank.Address1 = bankPropertyValue.address1?.newValue;
  bank.Address2 = bankPropertyValue.address2?.newValue;
  bank.City = bankPropertyValue.city?.newValue;
  bank.State = bankPropertyValue.state?.newValue;
  bank.ZipCode = bankPropertyValue.zipCode?.newValue;
  bank.ABAWire = bankPropertyValue.aBAWire?.newValue;
  bank.IBANCode = bankPropertyValue.ibanCode?.newValue;
  bank.Last4IBANCode = bankPropertyValue.last4IBANCode?.newValue;
  bank.SwiftCode = bankPropertyValue.swiftCode?.newValue;
  bank.SortCode = bankPropertyValue.sortCode?.newValue;
  bank.VATNumber = bankPropertyValue.vATNumber?.newValue;
  bank.ABANumber = bankPropertyValue.abaNumber?.newValue;
  bank.ABARoutingNumber = bankPropertyValue.aBARoutingNumber?.newValue;
  bank.AccountNumber = bankPropertyValue.accountNumber?.newValue;
  bank.Last4AccountNumber = bankPropertyValue.last4AccountNumber?.newValue;
  bank.Last4IntermediaryBankAccount = bankPropertyValue.last4IntermediaryBankAccount?.newValue;
  bank.MethodOfPaymentID = bankPropertyValue.paymentMethodID?.newValue;
  bank.Comments = bankPropertyValue.comments?.newValue;
  bank.Documents = bankPropertyValue.documents?.newValue;
  bank.ModifyDate = bankPropertyValue.modifyDate?.newValue;
  bank.ModifiedBy = bankPropertyValue.modifiedBy?.newValue;
  bank.CreateDate = bankPropertyValue.createDate?.newValue;
  bank.CreatedBy = bankPropertyValue.createdBy?.newValue;
  bank.IsNewBankAccount = bankPropertyValue.isNewBankAccount?.newValue;
  bank.IsNewIntermediaryBankAccount = bankPropertyValue.isNewIntermediaryBankAccount?.newValue;
  bank.IsIntermediaryBanking = bankPropertyValue.isIntermediaryBanking?.newValue;
  bank.IntermediaryPaymentOrigin = bankPropertyValue.intermediaryPaymentOrigin?.newValue;
  bank.IntermediaryBankName = bankPropertyValue.intermediaryBankName?.newValue;
  bank.IntermediaryBankRouting = bankPropertyValue.intermediaryBankRouting?.newValue;
  bank.IntermediaryBankSwiftCode = bankPropertyValue.intermediaryBankSwiftCode?.newValue;
  bank.IntermediaryBankAccount = bankPropertyValue.intermediaryBankAccount?.newValue;
  bank.IntermediaryAddress1 = bankPropertyValue.intermediaryAddress1?.newValue;
  bank.IntermediaryAddress2 = bankPropertyValue.intermediaryAddress2?.newValue;
  bank.IntermediaryCity = bankPropertyValue.intermediaryCity?.newValue;
  bank.IntermediaryState = bankPropertyValue.intermediaryState?.newValue;
  bank.IntermediaryZipCode = bankPropertyValue.intermediaryZipCode?.newValue;
  bank.IntermediaryCountryCode = bankPropertyValue.intermediaryCountryCode?.newValue;
  bank.VerificationStatus = bankPropertyValue.verificationStatus?.newValue;
  bank.VerificationTransactionId = bankPropertyValue.verificationTransactionId?.newValue;

  // Calculated values
  bank.AccountNumberExists = bank.AccountNumber ? true : false;
  bank.IBANCodeExists = bank.IBANCode ? true : false;
  bank.IntermediaryBankAccountExists = bank.IntermediaryBankAccount ? true : false;

  return bank;
}

export const getSupplierTax = (taxPropertyValue: SupplierTaxPropertyValues): SupplierTax => {
  const tax = new SupplierTax();
  if (!taxPropertyValue) {
    return tax;
  }
  tax.ID = taxPropertyValue.id?.newValue;
  tax.StageID = taxPropertyValue.stageID?.newValue;
  tax.Operation = taxPropertyValue.operation?.newValue;
  tax.FederalTaxID = taxPropertyValue?.federalTaxID.newValue;
  tax.TaxIDTypes = taxPropertyValue.taxIDTypes?.newValue;
  tax.ID = taxPropertyValue.id?.newValue;
  tax.StageID = taxPropertyValue.stageID?.newValue;
  tax.IsRelative = taxPropertyValue.isRelative?.newValue;
  tax.TaxName = taxPropertyValue.taxName?.newValue;
  tax.Corporate = taxPropertyValue.corporate?.newValue;
  tax.Tax1099Box = taxPropertyValue.tax1099Box?.newValue?.toString();
  tax.TaxCountry = taxPropertyValue.taxCountry?.newValue;
  tax.W9Received = taxPropertyValue.w9Received?.newValue;
  tax.W8Received = taxPropertyValue.w8Received?.newValue;
  tax.Documents = taxPropertyValue.documents?.newValue;
  tax.ForiegnEntity = taxPropertyValue.foriegnEntity?.newValue;
  tax.VATNumber = taxPropertyValue.vatNumber?.newValue;
  tax.ModifiedBy = taxPropertyValue.modifiedBy?.newValue;
  tax.ModifyDate = taxPropertyValue.modifyDate?.newValue;
  tax.CreateDate = taxPropertyValue.createDate?.newValue;
  tax.CreatedBy = taxPropertyValue.createdBy?.newValue;
  tax.Classification = taxPropertyValue.classification?.newValue;
  tax.ClassificationType = taxPropertyValue.classificationType?.newValue;
  tax.OtherCompany = taxPropertyValue.otherCompany?.newValue;
  tax.WithHoldingSelection = taxPropertyValue.withHoldingSelection?.newValue;
  tax.IsNewRecord = taxPropertyValue.isNewRecord?.newValue;
  tax.FileFederalTax = taxPropertyValue.fileFederalTax?.newValue;
  tax.EntityType = taxPropertyValue.entityType?.newValue;
  tax.CompanyType = taxPropertyValue.companyType?.newValue;
  tax.WithHoldingResponse = taxPropertyValue.withHoldingResponse.newValue;
  tax.AdditionalTaxIds = taxPropertyValue.additionalTaxIds?.newValue;
  return tax;
}

export const getSupplierDiversity = (bankPropertyValue: SupplierDiversityPropertyValue): SupplierDiversity => {
  const bank = new SupplierDiversity();
  if (!bankPropertyValue) {
    return bank;
  }
  bank.Operation = bankPropertyValue.operation?.newValue;
  bank.Operation = bankPropertyValue?.operation?.newValue;
  bank.IsDiversityOwned = bankPropertyValue?.isDiversityOwned?.newValue;
  bank.DiversityTypes = bankPropertyValue?.diversityTypes?.newValue;
  bank.OwnershipPercentage = bankPropertyValue?.ownershipPercentage?.newValue;
  bank.IsCouncilCertified = bankPropertyValue?.isCouncilCertified?.newValue;
  bank.CouncilCertifications = bankPropertyValue?.councilCertifications?.newValue;
  bank.IsGovtCertified = bankPropertyValue?.isGovtCertified?.newValue;
  bank.GovtCertifications = bankPropertyValue?.govtCertifications?.newValue;
  bank.DocumentIds = bankPropertyValue?.documentIds?.newValue;
  bank.Documents = bankPropertyValue?.documents?.newValue;

  return bank;
}

export const getSupplierBankMaster = (bankPropertyValue): SupplierBank => {
  const bank = new SupplierBank();
  if (!bankPropertyValue) {
    return bank;
  }
  bank.Operation = bankPropertyValue.operation;
  bank.SupplierCode = bankPropertyValue?.supplierCode;
  bank.PaymentOrigin = bankPropertyValue.paymentOrigin;
  bank.ID = bankPropertyValue.id;
  bank.StageID = bankPropertyValue.stageID;
  bank.BankName = bankPropertyValue.bankName;
  bank.BankLocalName = bankPropertyValue.bankLocalName;
  bank.BeneficiaryNameEnglish = bankPropertyValue.beneficiaryNameEnglish;
  bank.BeneficiaryNameDifferentReason = bankPropertyValue.beneficiaryNameDifferentReason;
  bank.BeneficiaryNameDifferentReasonComment = bankPropertyValue.beneficiaryNameDifferentReasonComment;
  bank.BeneficiaryNameLocal = bankPropertyValue.beneficiaryNameLocal;
  bank.DisplayName = bankPropertyValue.displayName;
  bank.BankNickName = bankPropertyValue.bankNickName;
  bank.CurrencyCode = bankPropertyValue.currencyCode;
  bank.CountryCode = bankPropertyValue.countryCode;
  bank.MethodOfPaymentID = bankPropertyValue.methodOfPaymentID;
  bank.PaymentType = bankPropertyValue.paymentType;
  bank.BankAccountType = bankPropertyValue.bankAccountType;
  bank.BankAccountNameKatanaka = bankPropertyValue.bankAccountNameKatanaka;
  bank.Address1 = bankPropertyValue.address1;
  bank.Address2 = bankPropertyValue.address2;
  bank.City = bankPropertyValue.city;
  bank.State = bankPropertyValue.state;
  bank.ZipCode = bankPropertyValue.zipCode;
  bank.ABAWire = bankPropertyValue.aBAWire;
  bank.IBANCode = bankPropertyValue.ibanCode;
  bank.Last4IBANCode = bankPropertyValue.last4IBANCode;
  bank.SwiftCode = bankPropertyValue.swiftCode;
  bank.SortCode = bankPropertyValue.sortCode;
  bank.VATNumber = bankPropertyValue.vATNumber;
  bank.ABANumber = bankPropertyValue.abaNumber;
  bank.ABARoutingNumber = bankPropertyValue.aBARoutingNumber;
  bank.AccountNumber = bankPropertyValue.accountNumber;
  bank.Last4AccountNumber = bankPropertyValue.last4AccountNumber;
  bank.Last4IntermediaryBankAccount = bankPropertyValue.last4IntermediaryBankAccount;
  bank.MethodOfPaymentID = bankPropertyValue.paymentMethodID;
  bank.Comments = bankPropertyValue.comments;
  bank.Documents = bankPropertyValue.documents;
  bank.ModifyDate = bankPropertyValue.modifyDate;
  bank.ModifiedBy = bankPropertyValue.modifiedBy;
  bank.CreateDate = bankPropertyValue.createDate;
  bank.CreatedBy = bankPropertyValue.createdBy;
  bank.IsNewBankAccount = bankPropertyValue.isNewBankAccount;
  bank.IsNewIntermediaryBankAccount = bankPropertyValue.isNewIntermediaryBankAccount;
  bank.IsIntermediaryBanking = bankPropertyValue.isIntermediaryBanking;
  bank.IntermediaryPaymentOrigin = bankPropertyValue.intermediaryPaymentOrigin
  bank.IntermediaryBankName = bankPropertyValue.intermediaryBankName;
  bank.IntermediaryBankRouting = bankPropertyValue.intermediaryBankRouting;
  bank.IntermediaryBankSwiftCode = bankPropertyValue.intermediaryBankSwiftCode;
  bank.IntermediaryBankAccount = bankPropertyValue.intermediaryBankAccount;
  bank.IntermediaryAddress1 = bankPropertyValue.intermediaryAddress1;
  bank.IntermediaryAddress2 = bankPropertyValue.intermediaryAddress2;
  bank.IntermediaryCity = bankPropertyValue.intermediaryCity;
  bank.IntermediaryState = bankPropertyValue.intermediaryState;
  bank.IntermediaryZipCode = bankPropertyValue.intermediaryZipCode;
  bank.IntermediaryCountryCode = bankPropertyValue.intermediaryCountryCode;
  bank.VerificationStatus = bankPropertyValue.verificationStatus;
  bank.VerificationTransactionId = bankPropertyValue.verificationTransactionId;

  // Calculated values
  bank.AccountNumberExists = bank.AccountNumber ? true : false;
  bank.IBANCodeExists = bank.IBANCode ? true : false;
  bank.IntermediaryBankAccountExists = bank.IntermediaryBankAccount ? true : false;

  return bank;
}

export const getSupplierBankPropertyValues = (bank: SupplierBank, bankPropertyValue: SupplierBankPropertyValue) => {
  bankPropertyValue = new SupplierBankPropertyValue();
  bankPropertyValue.operation.newValue = bank.Operation;
  bankPropertyValue.supplierCode = bank.SupplierCode;
  bankPropertyValue.paymentOrigin.newValue = bank.PaymentOrigin;
  bankPropertyValue.id.newValue = bank.ID;
  bankPropertyValue.stageID.newValue = bank.StageID;
  bankPropertyValue.bankName.newValue = bank.BankName;
  bankPropertyValue.bankLocalName.newValue = bank.BankLocalName;
  bankPropertyValue.beneficiaryNameEnglish.newValue = bank.BeneficiaryNameEnglish;
  bankPropertyValue.beneficiaryNameDifferentReason.newValue = bank.BeneficiaryNameDifferentReason;
  bankPropertyValue.beneficiaryNameDifferentReasonComment.newValue = bank.BeneficiaryNameDifferentReasonComment;
  bankPropertyValue.beneficiaryNameLocal.newValue = bank.BeneficiaryNameLocal;
  bankPropertyValue.displayName.newValue = bank.DisplayName;
  bankPropertyValue.bankNickName.newValue = bank.BankNickName;
  bankPropertyValue.currencyCode.newValue = bank.CurrencyCode;
  bankPropertyValue.countryCode.newValue = bank.CountryCode;
  bankPropertyValue.methodOfPaymentID.newValue = bank.MethodOfPaymentID;
  bankPropertyValue.paymentType.newValue = bank.PaymentType;
  bankPropertyValue.bankAccountType.newValue = bank.BankAccountType;
  bankPropertyValue.bankAccountNameKatanaka.newValue = bank.BankAccountNameKatanaka;
  bankPropertyValue.address1.newValue = bank.Address1;
  bankPropertyValue.address2.newValue = bank.Address2;
  bankPropertyValue.city.newValue = bank.City;
  bankPropertyValue.state.newValue = bank.State;
  bankPropertyValue.zipCode.newValue = bank.ZipCode;
  bankPropertyValue.aBAWire.newValue = bank.ABAWire;
  bankPropertyValue.ibanCode.newValue = bank.IBANCode;
  bankPropertyValue.last4IBANCode.newValue = bank.Last4IBANCode;
  bankPropertyValue.swiftCode.newValue = bank.SwiftCode;
  bankPropertyValue.sortCode.newValue = bank.SortCode;
  bankPropertyValue.vATNumber.newValue = bank.VATNumber;
  bankPropertyValue.abaNumber.newValue = bank.ABANumber;
  bankPropertyValue.aBARoutingNumber.newValue = bank.ABARoutingNumber;
  bankPropertyValue.accountNumber.newValue = bank.AccountNumber;
  bankPropertyValue.last4AccountNumber.newValue = bank.Last4AccountNumber;
  bankPropertyValue.last4IntermediaryBankAccount.newValue = bank.Last4IntermediaryBankAccount;
  bankPropertyValue.paymentMethodID.newValue = bank.MethodOfPaymentID;
  bankPropertyValue.comments.newValue = bank.Comments;
  bankPropertyValue.documents.newValue = bank.Documents;
  bankPropertyValue.modifyDate.newValue = bank.ModifyDate;
  bankPropertyValue.modifiedBy.newValue = bank.ModifiedBy;
  bankPropertyValue.createDate.newValue = bank.CreateDate;
  bankPropertyValue.createdBy.newValue = bank.CreatedBy;
  bankPropertyValue.isNewBankAccount.newValue = bank.IsNewBankAccount;
  bankPropertyValue.isNewIntermediaryBankAccount.newValue = bank.IsNewIntermediaryBankAccount;
  bankPropertyValue.isIntermediaryBanking.newValue = bank.IsIntermediaryBanking;
  bankPropertyValue.intermediaryPaymentOrigin.newValue = bank.IntermediaryPaymentOrigin;
  bankPropertyValue.intermediaryBankName.newValue = bank.IntermediaryBankName;
  bankPropertyValue.intermediaryBankRouting.newValue = bank.IntermediaryBankRouting;
  bankPropertyValue.intermediaryBankSwiftCode.newValue = bank.IntermediaryBankSwiftCode;
  bankPropertyValue.intermediaryBankAccount.newValue = bank.IntermediaryBankAccount;
  bankPropertyValue.intermediaryAddress1.newValue = bank.IntermediaryAddress1;
  bankPropertyValue.intermediaryAddress2.newValue = bank.IntermediaryAddress2;
  bankPropertyValue.intermediaryCity.newValue = bank.IntermediaryCity;
  bankPropertyValue.intermediaryState.newValue = bank.IntermediaryState;
  bankPropertyValue.intermediaryZipCode.newValue = bank.IntermediaryZipCode;
  bankPropertyValue.intermediaryCountryCode.newValue = bank.IntermediaryCountryCode;
  bankPropertyValue.verificationStatus.newValue = bank.VerificationStatus;
  bankPropertyValue.verificationTransactionId.newValue = bank.VerificationTransactionId;

  return bankPropertyValue;
}

export const getSupplierTaxPropertyValues = (tax: SupplierTax, taxPropertyValue: SupplierTaxPropertyValues) => {
  taxPropertyValue = new SupplierTaxPropertyValues();
  taxPropertyValue.operation.newValue = tax.Operation;
  taxPropertyValue.id.newValue = tax.ID;
  taxPropertyValue.stageID.newValue = tax.StageID;
  taxPropertyValue.taxName.newValue = tax.TaxName;
  taxPropertyValue.supplierID.newValue = tax.ID;
  taxPropertyValue.federalTaxID.newValue = tax.FederalTaxID;
  taxPropertyValue.vatNumber.newValue = tax.VATNumber;
  taxPropertyValue.taxIDTypes.newValue = tax.TaxIDTypes;
  taxPropertyValue.corporate.newValue = tax.Corporate;
  taxPropertyValue.tax1099Box.newValue = tax.Tax1099Box;
  taxPropertyValue.w9Received.newValue = tax.W9Received;
  taxPropertyValue.w8Received.newValue = tax.W8Received;
  taxPropertyValue.isRelative.newValue = tax.IsRelative;
  taxPropertyValue.documents.newValue = tax.Documents;
  taxPropertyValue.modifyDate.newValue = tax.ModifyDate;
  taxPropertyValue.modifiedBy.newValue = tax.ModifiedBy;
  taxPropertyValue.createDate.newValue = tax.CreateDate;
  taxPropertyValue.createdBy.newValue = tax.CreatedBy;
  taxPropertyValue.taxCountry.newValue = tax.TaxCountry;
  taxPropertyValue.foriegnEntity.newValue = tax.ForiegnEntity;
  taxPropertyValue.withHoldingSelection.newValue = tax.WithHoldingSelection;
  taxPropertyValue.classification.newValue = tax.Classification;
  taxPropertyValue.classificationType.newValue = tax.ClassificationType;
  taxPropertyValue.isNewRecord.newValue = tax.IsNewRecord;
  taxPropertyValue.fileFederalTax.newValue = tax.FileFederalTax;
  taxPropertyValue.entityType.newValue = tax.EntityType;
  taxPropertyValue.withHoldingResponse.newValue = tax.WithHoldingResponse;
  taxPropertyValue.companyType.newValue = tax.CompanyType;
  taxPropertyValue.additionalTaxIds.newValue = tax.AdditionalTaxIds;
  return taxPropertyValue;
}

export const getSupplierTaxValues = (tax: SupplierTaxInfo, supplierTax: SupplierTax) => {
  supplierTax.Operation = tax.Operation;
  supplierTax.TaxName = tax.TaxName;
  supplierTax.FederalTaxID = tax.FederalTaxID;
  supplierTax.VATNumber = tax.VATNumber;
  supplierTax.TaxIDTypes = tax.TaxIDTypes;
  supplierTax.Corporate = tax.Corporate;
  supplierTax.Tax1099Box = tax.Tax1099Box;
  supplierTax.W9Received = tax.W9Received;
  supplierTax.W8Received = tax.W8Received;
  supplierTax.Documents = tax.Documents;
  supplierTax.TaxCountry = tax.TaxCountry;
  supplierTax.WithHoldingSelection = tax.WithHoldingSelection;
  supplierTax.Classification = tax.Classification;
  supplierTax.ClassificationType = tax.ClassificationType;
  supplierTax.OtherCompany = tax.OtherCompany;
  supplierTax.IsNewRecord = tax.IsNewRecord;
  supplierTax.FileFederalTax = tax.FileFederalTax;
  supplierTax.EntityType = tax.EntityType;
  supplierTax.WithHoldingResponse = tax.WithHoldingResponse;
  supplierTax.CompanyType = tax.CompanyType;
  supplierTax.AdditionalTaxIds = tax.AdditionalTaxIds;
  return supplierTax;
}

export const getSupplierTaxInfoValues = (supplierTax: SupplierTax) => {
  let tax = new SupplierTaxInfo();
  tax.ID = supplierTax.ID;
  tax.Operation = supplierTax.Operation;
  tax.TaxName = supplierTax.TaxName;
  tax.FederalTaxID = supplierTax.FederalTaxID;
  tax.VATNumber = supplierTax.VATNumber;
  tax.TaxIDTypes = supplierTax.TaxIDTypes;
  tax.Corporate = supplierTax.Corporate;
  tax.Tax1099Box = supplierTax.Tax1099Box;
  tax.W9Received = supplierTax.W9Received;
  tax.W8Received = supplierTax.W8Received;
  tax.Documents = supplierTax.Documents;
  tax.TaxCountry = supplierTax.TaxCountry;
  tax.WithHoldingSelection = supplierTax.WithHoldingSelection;
  tax.Classification = supplierTax.Classification;
  tax.ClassificationType = supplierTax.ClassificationType;
  tax.OtherCompany = supplierTax.OtherCompany;
  tax.IsNewRecord = supplierTax.IsNewRecord;
  tax.FileFederalTax = supplierTax.FileFederalTax;
  tax.EntityType = supplierTax.EntityType;
  tax.CompanyType = supplierTax.CompanyType;
  tax.WithHoldingResponse = supplierTax.WithHoldingResponse
  tax.AdditionalTaxIds = supplierTax.AdditionalTaxIds;
  return tax;
}

export const getSupplierDocumentPropertyValues = (document: SupplierDocument, documentPropertyValue: SupplierDocumentPropertyValues) => {
  documentPropertyValue = new SupplierDocumentPropertyValues();
  documentPropertyValue.operation.newValue = document.Operation;
  documentPropertyValue.isTouched = true;
  documentPropertyValue.documentID.newValue = document.DocumentID;
  documentPropertyValue.documentVersion.newValue = document.DocumentVersion;
  documentPropertyValue.referenceSystem.newValue = document.ReferenceSystem;
  documentPropertyValue.id.newValue = document.ID;
  documentPropertyValue.stageID.newValue = document.StageID;
  documentPropertyValue.fileName.newValue = document.FileName;
  documentPropertyValue.documentPath.newValue = document.DocumentPath;
  documentPropertyValue.eSignAgreementId.newValue = document.ESignAgreementId;
  documentPropertyValue.documentType.newValue = document.DocumentType;
  documentPropertyValue.isValid.newValue = document.IsValid;
  documentPropertyValue.isContractNotRequired.newValue = document.IsContractNotRequired;
  documentPropertyValue.noEffectiveDate.newValue = document.NoEffectiveDate;
  documentPropertyValue.noExpirationDate.newValue = document.NoExpirationDate;
  documentPropertyValue.noSignedDate.newValue = document.NoSignedDate;
  documentPropertyValue.attachment.newValue = document.Attachment;
  documentPropertyValue.attachmentPath.newValue = document.AttachmentPath;
  documentPropertyValue.signedDate.newValue = document.SignedDate;
  documentPropertyValue.effectiveDate.newValue = document.EffectiveDate;
  documentPropertyValue.expiryDate.newValue = document.ExpiryDate;
  documentPropertyValue.modifiedDate.newValue = document.ModifiedDate;

  return documentPropertyValue;
}

export function convertSupplierDocumentPropertyValuesToSupplierDocument (documentPropertyValue: SupplierDocumentPropertyValues): SupplierDocument {
  let document = new SupplierDocument();
  if(!documentPropertyValue) return document;
  document.Operation = documentPropertyValue.operation?.newValue;
  document.DocumentID = documentPropertyValue.documentID?.newValue;
  document.DocumentVersion = documentPropertyValue.documentVersion?.newValue;
  document.ReferenceSystem = documentPropertyValue.referenceSystem?.newValue;
  document.ID = documentPropertyValue.id?.newValue;
  document.StageID = documentPropertyValue.stageID?.newValue;
  document.FileName = documentPropertyValue.fileName?.newValue;
  document.DocumentPath = documentPropertyValue.documentPath?.newValue;
  document.ESignAgreementId = documentPropertyValue.eSignAgreementId?.newValue;
  document.DocumentType = documentPropertyValue.documentType?.newValue;
  document.IsValid = documentPropertyValue.isValid?.newValue;
  document.IsContractNotRequired = documentPropertyValue.isContractNotRequired?.newValue;
  document.NoEffectiveDate = documentPropertyValue.noEffectiveDate?.newValue;
  document.NoExpirationDate = documentPropertyValue.noExpirationDate?.newValue;
  document.NoSignedDate = documentPropertyValue.noSignedDate?.newValue;
  document.Attachment = documentPropertyValue.attachment?.newValue;
  document.AttachmentPath = documentPropertyValue.attachmentPath?.newValue;
  document.SignedDate = documentPropertyValue.signedDate?.newValue;
  document.EffectiveDate = documentPropertyValue.effectiveDate?.newValue;
  document.ExpiryDate = documentPropertyValue.expiryDate?.newValue;
  document.ModifiedDate = documentPropertyValue.modifiedDate?.newValue;

  return document;
}

export function getEnvironmentFromClientUrl(url: string): 'dev' | 'uat' | 'stg' | 'prd' {
  if (url?.includes('localhost') || url?.includes('dev')) {
    return 'dev';
  } else if (url?.includes('uat')) {
    return 'uat';
  } else if (url?.includes('stg')) {
    return 'stg';
  } else {
    return 'prd';
  }
}

export function isProdFromClientUrl(url: string): boolean {
  return getEnvironmentFromClientUrl(url) === 'prd';
}

export function htmlDecode(s: string): string {
  if (!s) {
    return '';
  } else if (typeof s != 'string') {
    return s;
  }
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&#34;/g, '"');
}


/**
* get scope
*/
export const getTemplateTranslationScope = (templateName: string, i18nKey: string): string => {
  const pascalCasedText = templateName?.replace(/\/./g, (x) => x[1].toUpperCase());
  if (pascalCasedText) {
    const transformedTemplateName = pascalCasedText.charAt(0).toUpperCase() + pascalCasedText.slice(1);
    return `taskTemplate${transformedTemplateName}.${i18nKey}`;
  }

  return '';
};


export function validateESignDocument(status: string): boolean {
  // check document: https://secure.na1.adobesign.com/public/docs/restapi/v6#DetailedParticipantSetInfoget_agreements_{agreementId}_members
  if (status === 'COMPLETED' || status === 'WAITING_FOR_OTHERS') {
    return true;
  } else {
    return false;
  }
}

export function convertKeysToCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((result, key) => {
      const camelCaseKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      result[camelCaseKey] = convertKeysToCamelCase(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
}

export function convertKeysFromPascalToCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysFromPascalToCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((result, key) => {
      const camelCaseKey = key.charAt(0).toLowerCase() + key.slice(1);
      result[camelCaseKey] = convertKeysFromPascalToCamelCase(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
}

export { deepfind, intersect, inBoth, GetFullAddress, GetFullAddressDetails, GetCountryDetails, GetFullName, getSupplierProfileAdmin };
export { validateAllFormFields };
export { ValidationMessages };
export { getNestedNode };
export { isNullOrEmpty };
export { updateValueAndValidity };
export { isNullOrUndefined };